import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import type { IncidentSeverityConfig } from '@isg/shared-types';
import { useSeviyeConfig, useSeviyeConfigYazma } from '../api/queries';
import { SIDDET_SIRA_ETIKET, TURETME_GIRDISI_ETIKET } from '../i18n/tr';
import { QueryStatus } from './parts';
import { ANLAM } from '../theme';

/**
 * OLAY ŞİDDET SEVİYESİ YAPILANDIRMASI (F3B·S3 — O6 + D8-D9; Kapsam v0.43
 * §16.8/3, §19/15). Yazma yalnız holding yöneticisinde (uç 403 verir; menü
 * görünürlüğü my-authorization'dan — karar guard'da).
 *
 * TABAN KURALLARI SUNUCUDADIR, EKRAN YALNIZ YANSITIR (§2.16):
 *   · KADEME SİLME SUNULMAZ — kullanılmış kademe SİLİNEMEZ (422, §19/15),
 *     GİZLENİR; ekran bunu bilgi satırı olarak söyler (D2 dili sunucudan).
 *   · SEVİYE ELLE YAZILAMAZ — türetme motoru tek yazıcıdır (O7); bu ekran
 *     yalnız kademe ADLARINI ve eşleme/eşik SATIRLARINI düzenler.
 *   · D8: maddi hasar + proses SATIRSIZ doğar (kütüphane eşik dağıtmaz) —
 *     boş kalması geçerlidir; eşiksiz sınıf türetmeye katılmaz + kayıtta
 *     uyarı görünür.
 */
export function OlaySeviyeScreen(): React.JSX.Element {
  const config = useSeviyeConfig();
  return (
    <Stack maw={980}>
      <Title order={3}>Olay şiddet seviyesi</Title>
      <QueryStatus queries={[config]} />
      {config.isSuccess && (
        <>
          <KademeListesi config={config.data} />
          <EslemeSatirlari config={config.data} />
        </>
      )}
    </Stack>
  );
}

/** Kademe adları + gizleme (O6 — sayı/ad müşterinin; sürüm mekanizması YOK). */
function KademeListesi({ config }: { config: IncidentSeverityConfig }): React.JSX.Element {
  const yaz = useSeviyeConfigYazma();
  const [adlar, setAdlar] = useState<Record<string, string>>({});

  return (
    <Card withBorder data-testid="seviye-kademeler">
      <Stack gap="xs">
        <Text fw={600}>Ölçek kademeleri (tenant başına TEK ölçek)</Text>
        <Text size="sm" c="dimmed">
          Kademe adları sizindir; ölçek holding genelinde TEKTİR (şirkete inmez — konsolidasyonun
          değişmezi). Silme yoktur: kullanılmış kademe gizlenir — gizlenen kademe yeni referans
          almaz, mevcut kayıtlar yaşar. Seviye elle yazılamaz; sistem sınıf girdilerinden türetir.
        </Text>
        <Table withTableBorder data-testid="seviye-kademe-tablosu">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Sıra</Table.Th>
              <Table.Th>Ad</Table.Th>
              <Table.Th>Durum</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {config.seviyeler.map((kademe) => (
              <Table.Tr key={kademe.id} data-testid="seviye-kademe-satiri">
                <Table.Td>{kademe.sira}</Table.Td>
                <Table.Td>
                  <TextInput
                    value={adlar[kademe.id] ?? kademe.ad}
                    onChange={(event) =>
                      setAdlar((eski) => ({ ...eski, [kademe.id]: event.currentTarget.value }))
                    }
                    data-testid={`seviye-kademe-ad-${kademe.sira}`}
                  />
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    {kademe.gizli && <Badge color={ANLAM.bilgi}>Gizli</Badge>}
                    {kademe.kullanimda && (
                      <Badge color={ANLAM.bilgi} variant="light">
                        Kullanımda
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" justify="flex-end">
                    <Switch
                      label="Gizli"
                      size="xs"
                      checked={kademe.gizli}
                      onChange={(event) =>
                        yaz.mutate({
                          tur: 'level',
                          id: kademe.id,
                          govde: { gizli: event.currentTarget.checked },
                        })
                      }
                      data-testid={`seviye-kademe-gizle-${kademe.sira}`}
                    />
                    <Button
                      size="xs"
                      variant="light"
                      loading={yaz.isPending}
                      disabled={(adlar[kademe.id] ?? kademe.ad) === kademe.ad}
                      onClick={() =>
                        yaz.mutate(
                          {
                            tur: 'level',
                            id: kademe.id,
                            govde: { ad: (adlar[kademe.id] ?? kademe.ad).trim() },
                          },
                          {
                            onSuccess: () => setAdlar(({ [kademe.id]: _eski, ...kalan }) => kalan),
                          },
                        )
                      }
                      data-testid={`seviye-kademe-kaydet-${kademe.sira}`}
                    >
                      Adı kaydet
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <YazmaHatasi hata={yaz.error} />
      </Stack>
    </Card>
  );
}

/**
 * Türetme satırları — sınıf başına EŞLEME (merdiven/yayılım birebir) veya
 * EŞİK (hasar/kayıp miktarı ≥) satırları; tip sınıfın girdisinden gelir
 * (sunucu işareti — ekran ikinci kural yazmaz, uyumsuz satır 422).
 */
function EslemeSatirlari({ config }: { config: IncidentSeverityConfig }): React.JSX.Element {
  const yaz = useSeviyeConfigYazma();
  const [sinifId, setSinifId] = useState<string | null>(null);
  const secili = config.siniflar.find((s) => s.id === sinifId) ?? null;
  const eslemeTipli = secili?.girdi === 'merdiven' || secili?.girdi === 'yayilim';
  const mevcutSatirlar = useMemo(
    () => config.kurallar.filter((k) => k.olaySinifiRefId === sinifId),
    [config.kurallar, sinifId],
  );
  // Eşleme düzenleyicisi: girdi sıra → seviye seçimi (yerel taslak).
  const [eslemeler, setEslemeler] = useState<Record<number, string | null> | null>(null);
  // Eşik düzenleyicisi: yerel satır listesi.
  const [esikler, setEsikler] = useState<
    { esikAlt: number | string; maddeSinifiRefId: string | null; seviyeId: string | null }[] | null
  >(null);

  const seviyeData = config.seviyeler
    .filter((s) => !s.gizli)
    .map((s) => ({ value: s.id, label: `${s.sira} — ${s.ad}` }));
  const girdiSiralari = secili?.girdi === 'merdiven' ? [1, 2, 3, 4, 5] : [1, 2, 3, 4, 5];
  const gecerliEslemeler =
    eslemeler ??
    Object.fromEntries(
      girdiSiralari.map((sira) => [
        sira,
        mevcutSatirlar.find((k) => k.girdiSira === sira)?.seviyeId ?? null,
      ]),
    );
  const gecerliEsikler =
    esikler ??
    mevcutSatirlar
      .filter((k) => k.esikAlt !== null)
      .map((k) => ({
        esikAlt: k.esikAlt as number,
        maddeSinifiRefId: k.maddeSinifiRefId,
        seviyeId: k.seviyeId,
      }));

  const kaydet = (): void => {
    if (!sinifId) return;
    const satirlar = eslemeTipli
      ? Object.entries(gecerliEslemeler)
          .filter(([, seviyeId]) => seviyeId !== null)
          .map(([sira, seviyeId]) => ({ girdiSira: Number(sira), seviyeId: seviyeId as string }))
      : gecerliEsikler
          .filter((satir) => satir.esikAlt !== '' && satir.seviyeId !== null)
          .map((satir) => ({
            esikAlt: Number(satir.esikAlt),
            maddeSinifiRefId: satir.maddeSinifiRefId,
            seviyeId: satir.seviyeId as string,
          }));
    yaz.mutate(
      { tur: 'rules', sinifRefId: sinifId, govde: { satirlar } },
      {
        onSuccess: () => {
          setEslemeler(null);
          setEsikler(null);
        },
      },
    );
  };

  return (
    <Card withBorder data-testid="seviye-esleme">
      <Stack gap="xs">
        <Text fw={600}>Sınıf → seviye türetme satırları</Text>
        <Text size="sm" c="dimmed">
          Kişi güvenliği/trafik merdivenden, çevre yayılım sınırından BİREBİR eşlenir (D9
          varsayılanı). Maddi hasar ve proses güvenliği eşik SATIRSIZ gelir — kütüphane eşik sayısı
          dağıtmaz (D8); eşiklerinizi siz tanımlarsınız. Eşiksiz sınıf türetmeye katılmaz ve kayıtta
          &quot;eşikleri tanımlanmamış&quot; uyarısı görünür — kayıt engellenmez.
        </Text>
        <Select
          label="Olay sınıfı"
          data={config.siniflar
            .filter((s) => s.girdi !== null)
            .map((s) => ({
              value: s.id,
              label: `${s.ad} (${TURETME_GIRDISI_ETIKET[s.girdi ?? ''] ?? s.girdi})`,
            }))}
          value={sinifId}
          onChange={(deger) => {
            setSinifId(deger);
            setEslemeler(null);
            setEsikler(null);
          }}
          data-testid="seviye-sinif-secimi"
          searchable
          maw={420}
        />
        {secili && eslemeTipli && (
          <Stack gap={4}>
            {girdiSiralari.map((sira) => (
              <Group key={sira} gap="xs">
                <Text size="sm" w={220}>
                  {secili.girdi === 'merdiven'
                    ? `${sira} — ${SIDDET_SIRA_ETIKET[sira] ?? sira}`
                    : `Yayılım ${sira}`}
                </Text>
                <Select
                  data={seviyeData}
                  clearable
                  value={gecerliEslemeler[sira] ?? null}
                  onChange={(deger) => setEslemeler({ ...gecerliEslemeler, [sira]: deger })}
                  data-testid={`seviye-esleme-${sira}`}
                  w={220}
                />
              </Group>
            ))}
          </Stack>
        )}
        {secili && !eslemeTipli && (
          <Stack gap={4}>
            {gecerliEsikler.map((satir, i) => (
              <Group key={i} gap="xs" align="end">
                <NumberInput
                  label="Eşik (≥)"
                  min={0}
                  value={satir.esikAlt}
                  onChange={(deger) =>
                    setEsikler(
                      gecerliEsikler.map((s, j) => (j === i ? { ...s, esikAlt: deger } : s)),
                    )
                  }
                  w={140}
                />
                {secili.girdi === 'esik_tablosu' && (
                  <Select
                    label="Madde sınıfı (boş = tümü)"
                    clearable
                    data={config.maddeSiniflari.map((m) => ({ value: m.id, label: m.ad }))}
                    value={satir.maddeSinifiRefId}
                    onChange={(deger) =>
                      setEsikler(
                        gecerliEsikler.map((s, j) =>
                          j === i ? { ...s, maddeSinifiRefId: deger } : s,
                        ),
                      )
                    }
                    w={220}
                  />
                )}
                <Select
                  label="Seviye"
                  data={seviyeData}
                  value={satir.seviyeId}
                  onChange={(deger) =>
                    setEsikler(
                      gecerliEsikler.map((s, j) => (j === i ? { ...s, seviyeId: deger } : s)),
                    )
                  }
                  data-testid={`seviye-esik-seviye-${i}`}
                  w={200}
                />
                <Button
                  variant="subtle"
                  color={ANLAM.engel}
                  onClick={() => setEsikler(gecerliEsikler.filter((_, j) => j !== i))}
                >
                  Satırı sil
                </Button>
              </Group>
            ))}
            <Group>
              <Button
                variant="light"
                data-testid="seviye-esik-satir-ekle"
                onClick={() =>
                  setEsikler([
                    ...gecerliEsikler,
                    { esikAlt: '', maddeSinifiRefId: null, seviyeId: null },
                  ])
                }
              >
                Eşik satırı ekle
              </Button>
            </Group>
          </Stack>
        )}
        {secili && (
          <Group>
            <Button loading={yaz.isPending} onClick={kaydet} data-testid="seviye-esleme-kaydet">
              Satırları kaydet
            </Button>
          </Group>
        )}
        <YazmaHatasi hata={yaz.error} />
      </Stack>
    </Card>
  );
}

function YazmaHatasi({ hata }: { hata: Error | null }): React.JSX.Element | null {
  if (!hata) return null;
  return (
    <Text size="sm" c={ANLAM.engel} role="alert" data-testid="seviye-yazma-hatasi">
      {hata.message}
    </Text>
  );
}
